import React, { useCallback, useMemo } from "react";

import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";

import RankedQueryCard from "../../charts/RankedQueryCard";
import { offersPurchasesQuery } from "../offerAnalytics.queries";
import { buildSalesByOffer, filterByOffer } from "../offerAnalytics.utils";
import useMissingOfferLabel from "../useMissingOfferLabel";

import type { JSX } from "react";

import type { GetOffersPurchasesResponse } from "@/src/api/StatsAPI.types";

import type { ChartSeries } from "../../charts/charts.types";
import type {
  OfferAnalyticsFilterProps,
  SalesSeriesKey,
} from "../offerAnalytics.types";

function SalesByOfferCard({ filters }: OfferAnalyticsFilterProps): JSX.Element {
  const { projectSlug } = useParams<{ projectSlug: string }>();
  const { t } = useTranslation("editor-analytics");
  const { offerUuid } = filters;
  const getMissingOfferLabel = useMissingOfferLabel();

  const seriesList = useMemo(
    (): ChartSeries<SalesSeriesKey>[] => [
      {
        key: "direct",
        label: t("offerAnalytics.series.direct"),
        color: "var(--chart-1)",
      },
      {
        key: "gifts",
        label: t("offerAnalytics.series.gifts"),
        color: "var(--chart-2)",
      },
    ],
    [t],
  );
  const buildRankedList = useCallback(
    (response: GetOffersPurchasesResponse | undefined) =>
      buildSalesByOffer(
        filterByOffer(
          response?.data ?? [],
          offerUuid,
          (purchase) => purchase.offer_uuid,
        ),
        getMissingOfferLabel,
      ),
    [offerUuid, getMissingOfferLabel],
  );

  return (
    <RankedQueryCard
      buildRankedList={buildRankedList}
      query={offersPurchasesQuery(projectSlug, filters)}
      seriesList={seriesList}
      title={t("offerAnalytics.charts.salesByOffer")}
    />
  );
}

export default SalesByOfferCard;
